import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { firestoreService } from "@/services/firestoreService";

interface BlazerexArticle {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  summary?: string;
  author?: string;
  url?: string;
  imageUrl?: string;
  tags?: string[];
  category?: string;
  publishedAt: any;
  createdAt: any;
  updatedAt: any;
}

export const BlazerexArticlePage: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<BlazerexArticle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (articleId) {
      loadArticle();
    }
  }, [articleId]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const articleData = await firestoreService.getDocument<BlazerexArticle>(
        "blazerexArticles",
        articleId!
      );
      setArticle(articleData);
    } catch (error) {
      console.error("Failed to load article:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <Card>
            <div className="text-center py-12">
              <h2 className="h2 text-2xl mb-4">Article Not Found</h2>
              <p className="body-text text-gray-600 mb-6">
                The article you're looking for doesn't exist.
              </p>
              <Button variant="primary" onClick={() => navigate("/news")}>
                Back to News
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const publishedDate = article.publishedAt?.toDate
    ? article.publishedAt.toDate()
    : new Date(article.publishedAt);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button
          variant="outline"
          onClick={() => navigate("/news")}
          className="mb-6"
        >
          ← Back to News
        </Button>

        <Card className="p-8">
          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-64 object-cover rounded-lg mb-6"
            />
          )}

          <div className="mb-4">
            {article.category && (
              <span className="inline-block px-3 py-1 bg-blazerex-green text-blazerex-black rounded-full text-sm font-semibold mr-2">
                {article.category}
              </span>
            )}
            <span className="body-text text-gray-500">
              {publishedDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>

          <h1 className="h1 text-4xl mb-4">{article.title}</h1>

          {article.author && (
            <p className="body-text text-gray-600 mb-6">
              By {article.author}
            </p>
          )}

          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          <div
            className="prose prose-lg max-w-none body-text text-gray-700"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {article.url && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blazerex-green hover:underline"
              >
                Read original article →
              </a>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

